const DOWNLOAD_URL_DEV = 'https://app.difisec.vip/dev/';
const DOWNLOAD_URL_PRE = 'https://app.difisec.vip/pre/';
const OPEN_APP_FALLBACK_MS = 1800;

function resolveViteMode(): string {
  return String(import.meta.env.VITE_MODE || '')
    .replace(/['"]/g, '')
    .toLowerCase();
}

type AppLaunchConfig = {
  scheme: string;
  packageName: string;
};

function resolveAppLaunchConfig(): AppLaunchConfig {
  const mode = resolveViteMode();
  if (mode === 'pre') return { scheme: 'ux-pre', packageName: 'com.difi.app' };
  if (mode === 'product' || mode === 'pro' || mode === 'production') {
    return { scheme: 'ux', packageName: 'com.difi.uxcom' };
  }
  return { scheme: 'ux-dev', packageName: 'com.difi.app.dev' };
}

function defaultStoreDownloadUrl(): string {
  const mode = resolveViteMode();
  if (mode === 'pre' || mode === 'product' || mode === 'pro' || mode === 'production') {
    return DOWNLOAD_URL_PRE;
  }
  return DOWNLOAD_URL_DEV;
}

/** 商店 / 安装包页（VITE_APP_DOWNLOAD_URL） */
export function getStoreDownloadUrl(): string {
  return String(import.meta.env.VITE_APP_DOWNLOAD_URL || '').trim() || defaultStoreDownloadUrl();
}

/** @deprecated 使用 getStoreDownloadUrl；保留别名避免外部旧引用 */
export function getAppDownloadUrl(): string {
  return getStoreDownloadUrl();
}

function resolveMainSiteOrigin(): string {
  const raw = String(import.meta.env.VITE_BASE_URL || '')
    .trim()
    .replace(/\/$/, '');
  if (/^https?:\/\//i.test(raw)) return raw;
  if (typeof window !== 'undefined') {
    if (raw.startsWith('/')) return `${window.location.origin}${raw}`;
    return window.location.origin;
  }
  return raw || '';
}

/** 主站 H5 下载引导页 `/app-download` */
export function getH5AppDownloadPageUrl(): string {
  return resolveMainH5Href('/app-download');
}

/** 主站 H5 任意 path（如 `/open-progress`） */
export function resolveMainH5Href(pathAndQuery: string): string {
  const value = pathAndQuery.trim() || '/';
  const path = value.startsWith('/') ? value : `/${value}`;
  const origin = resolveMainSiteOrigin();
  return origin ? `${origin}${path}` : path;
}

function nativePathToHost(nativePath?: string | null): string {
  const value = nativePath?.trim() || '';
  if (!value || value === '/') return '';
  return value.replace(/^\//, '');
}

function buildSchemeLink(nativePath?: string | null): string {
  const { scheme } = resolveAppLaunchConfig();
  const host = nativePathToHost(nativePath);
  return host ? `${scheme}://${host}` : `${scheme}://`;
}

function buildAndroidIntent(nativePath?: string | null, fallbackUrl?: string): string {
  const { scheme, packageName } = resolveAppLaunchConfig();
  const host = nativePathToHost(nativePath) || 'launch';
  const fallback = encodeURIComponent(fallbackUrl || getH5AppDownloadPageUrl());
  return `intent://${host}#Intent;scheme=${scheme};package=${packageName};S.browser_fallback_url=${fallback};end`;
}

function isAndroid(): boolean {
  return /android/i.test(navigator.userAgent || '');
}

/** 调试页：当前环境唤起配置与将要跳转的链接（不发起跳转） */
export function getAppLaunchDebugInfo(nativePath?: string | null, fallbackUrl?: string) {
  const mode = resolveViteMode();
  const config = resolveAppLaunchConfig();
  const fallback = (fallbackUrl || '').trim() || getH5AppDownloadPageUrl();
  return {
    mode: mode || '(empty)',
    scheme: config.scheme,
    packageName: config.packageName,
    isAndroid: isAndroid(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent || '' : '',
    schemeLink: buildSchemeLink(nativePath),
    androidIntent: buildAndroidIntent(nativePath, fallback),
    fallbackUrl: fallback,
    storeDownloadUrl: getStoreDownloadUrl(),
    launchHref: isAndroid() ? buildAndroidIntent(nativePath, fallback) : buildSchemeLink(nativePath),
  };
}

export type OpenAppOrDownloadOptions = {
  /** 唤起失败后跳转；默认主站 `/app-download` */
  fallbackUrl?: string;
};

/**
 * 外部浏览器：先用 App scheme / Android Intent 唤起；约 1.8s 仍停在页上则去 fallback。
 * 已进入 App（页进入后台）则取消跳转。
 * 默认 fallback 为 H5 `/app-download`；下载页内调用时传入 getStoreDownloadUrl()。
 */
export function openAppOrDownload(
  nativePath?: string | null,
  options?: OpenAppOrDownloadOptions,
): void {
  const fallbackUrl = (options?.fallbackUrl || '').trim() || getH5AppDownloadPageUrl();
  const deepLink = buildSchemeLink(nativePath);
  let settled = false;
  let timer = 0;

  const cleanup = () => {
    if (timer) window.clearTimeout(timer);
    document.removeEventListener('visibilitychange', onHidden);
    window.removeEventListener('pagehide', settleLeave);
  };

  const settleLeave = () => {
    if (settled) return;
    settled = true;
    cleanup();
  };

  const goFallback = () => {
    if (settled || document.hidden) return;
    settled = true;
    cleanup();
    window.location.href = fallbackUrl;
  };

  const onHidden = () => {
    if (document.hidden) settleLeave();
  };

  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('pagehide', settleLeave);
  timer = window.setTimeout(goFallback, OPEN_APP_FALLBACK_MS);

  window.location.href = isAndroid() ? buildAndroidIntent(nativePath, fallbackUrl) : deepLink;
}

/** 当前详情页对应的 App 原生路由；没有活动 id 时只唤起 App */
export function resolveCurrentActivityNativePath(): string {
  try {
    const id = new URLSearchParams(window.location.search).get('id')?.trim();
    if (id) return `/profile/activity-detail?id=${encodeURIComponent(id)}`;
  } catch {
    /* ignore */
  }
  return '/';
}
